import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import ApplyCta from "./ApplyCta";

import silvanaRattiImg from "@/assets/carousel/silvana-ratti.png";
import wilmarImg from "@/assets/carousel/wilmar.png";
import laRecetaImg from "@/assets/carousel/la-receta.png";
import lucianaGardellaImg from "@/assets/carousel/luciana-gardella.png";
import habitoMarketImg from "@/assets/carousel/habito-market.png";
import pastunguiImg from "@/assets/carousel/pastungui.png";
import medinorImg from "@/assets/carousel/medinor.png";
import duomoPackagingImg from "@/assets/carousel/duomo-packaging.png";
import duomoImg from "@/assets/carousel/duomo.png";
import pegasoMercurioImg from "@/assets/carousel/pegaso-mercurio.png";
import sergioMurImg from "@/assets/carousel/sergio-mur.png";
import losFerrerImg from "@/assets/carousel/los-ferrer.png";
import vitiaImg from "@/assets/carousel/vitia.png";
import safeZoneImg from "@/assets/carousel/safe-zone.png";
import oralixImg from "@/assets/carousel/oralix.png";
import laRecetaLocalImg from "@/assets/carousel/la-receta-local.png";
import claudiaFerraraImg from "@/assets/carousel/claudia-ferrara.png";

// Row 1: intercalated industries
const row1 = [
  { image: silvanaRattiImg, name: "Silvana Ratti", industry: "Real Estate · Construcción" },
  { image: medinorImg, name: "Medinor", industry: "Salud" },
  { image: laRecetaImg, name: "La Receta", industry: "Alimentos" },
  { image: pegasoMercurioImg, name: "Pegaso · Grupo Mercurio", industry: "Transporte y Logística" },
  { image: losFerrerImg, name: "Los Ferrer", industry: "Estética" },
  { image: duomoImg, name: "Duomo Helados", industry: "Gastronomía" },
  { image: vitiaImg, name: "Vitia", industry: "Salud Integral" },
  { image: oralixImg, name: "Oralix", industry: "Odontología Especializada" },
];

// Row 2: intercalated industries (different order)
const row2 = [
  { image: lucianaGardellaImg, name: "Luciana Gardella", industry: "Consultora en Ciudadanías" },
  { image: sergioMurImg, name: "Sergio Mur", industry: "Cirugía Plástica" },
  { image: wilmarImg, name: "Wilmar", industry: "Exportación" },
  { image: safeZoneImg, name: "Safe Zone", industry: "Bar · Gaming · Esports" },
  { image: habitoMarketImg, name: "Hábito Market", industry: "Wellness" },
  { image: duomoPackagingImg, name: "Duomo Packaging", industry: "Gourmet" },
  { image: laRecetaLocalImg, name: "La Receta Local", industry: "Tienda de Alimentos" },
  { image: claudiaFerraraImg, name: "Claudia Ferrara", industry: "Coach de Propósito" },
  { image: pastunguiImg, name: "Pastungui", industry: "Gastronomía" },
];

const useAutoScroll = (direction: "left" | "right") => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let animationId: number;
    let scrollPos = direction === "left" ? 0 : el.scrollWidth / 2;
    const speed = direction === "left" ? 0.4 : -0.4;

    // Start at midpoint for reverse direction
    if (direction === "right") {
      el.scrollLeft = el.scrollWidth / 2;
    }

    const animate = () => {
      scrollPos += speed;
      const half = el.scrollWidth / 2;
      if (direction === "left" && scrollPos >= half) scrollPos = 0;
      if (direction === "right" && scrollPos <= 0) scrollPos = half;
      el.scrollLeft = scrollPos;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    const handleMouseEnter = () => cancelAnimationFrame(animationId);
    const handleMouseLeave = () => {
      scrollPos = el.scrollLeft;
      animationId = requestAnimationFrame(animate);
    };

    el.addEventListener("mouseenter", handleMouseEnter);
    el.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      cancelAnimationFrame(animationId);
      el.removeEventListener("mouseenter", handleMouseEnter);
      el.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [direction]);

  return ref;
};

const CarouselRow = ({
  projects,
  direction,
}: {
  projects: typeof row1;
  direction: "left" | "right";
}) => {
  const scrollRef = useAutoScroll(direction);
  const allProjects = [...projects, ...projects];

  return (
    <div
      ref={scrollRef}
      className="flex gap-5 overflow-hidden cursor-grab select-none"
      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
    >
      {allProjects.map((project, index) => (
        <div
          key={`${project.name}-${index}`}
          className="flex-shrink-0 w-60 md:w-72 group"
        >
          <div className="relative aspect-square rounded-2xl overflow-hidden border border-border bg-card shadow-sm group-hover:shadow-xl group-hover:border-primary/40 transition-all duration-500">
            <img
              src={project.image}
              alt={project.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-5">
              <h3 className="text-white font-display text-lg font-bold">{project.name}</h3>
              <p className="text-white/70 text-sm">{project.industry}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const IndustryCarousel = () => {
  return (
    <section className="py-16 md:py-24 bg-muted/30 overflow-hidden">
      <div className="container px-4 mb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-3">
            Desarrollos de Branding en clientes de <span className="text-gradient-gold">diversas industrias</span>
          </h2>
          <p className="text-sm font-medium tracking-widest uppercase text-muted-foreground">
            Portfolio Multisectorial
          </p>
        </motion.div>
      </div>

      <div className="space-y-5">
        <CarouselRow projects={row1} direction="left" />
        <CarouselRow projects={row2} direction="right" />
      </div>

      <div className="container px-4">
        <ApplyCta />
      </div>
    </section>
  );
};

export default IndustryCarousel;
